import { useState } from "react";
import { toast } from "sonner";
import { Mic2, Radio, TrendingUp, Users, CheckCircle2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";

const PERK_ICONS = [Radio, TrendingUp, Users, Mic2];

const empty = { name: "", email: "", show_name: "", category: "", pitch: "", links: "" };

export default function JoinPage() {
  const { homepage } = useSettings();
  const perks = homepage.join_perks || [];
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try { await api.post("/join", form); setDone(true); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setSending(false); }
  };

  const field = "mt-1.5 bg-white/5 border-white/10";

  return (
    <div className="min-h-screen flex flex-col" data-testid="join-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-12 pb-32">
        <div className="grid lg:grid-cols-[1fr_520px] gap-12 items-stretch">
          <div className="relative flex flex-col">
            <p className="eyebrow mb-3">{homepage.join_eyebrow || "Join The Underdog"}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">{homepage.join_title || "Got a show?"}<br /><span className="text-sky-400">{homepage.join_title_accent || "Run with our pack."}</span></h1>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-xl">{homepage.join_intro}</p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4 flex-1">
              {perks.map(({ title, text }, i) => {
                const Icon = PERK_ICONS[i % PERK_ICONS.length];
                return (
                <div key={title} className="card-surface rounded-2xl p-5 reveal" style={{ animationDelay: `${i * 70}ms` }} data-testid={`join-perk-${i}`}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#053671] text-sky-300"><Icon size={18} /></span>
                  <h3 className="mt-3 font-display font-bold">{title}</h3>
                  <p className="mt-1 text-sm text-slate-400 leading-relaxed">{text}</p>
                </div>
                );
              })}
            </div>
          </div>

          <div className="card-surface grain relative overflow-hidden rounded-3xl p-6 sm:p-8 flex flex-col" data-testid="join-form-card">
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#053671] blur-3xl opacity-50 pointer-events-none" />
            {done ? (
              <div className="relative flex-1 flex flex-col items-center justify-center text-center py-10" data-testid="join-success">
                <CheckCircle2 size={48} className="mx-auto text-sky-400" />
                <h2 className="mt-4 font-display text-2xl font-extrabold">Application received</h2>
                <p className="mt-2 text-sm text-slate-400">Thanks, {form.name.split(" ")[0]}. The Underdog team will review your pitch and reach out at {form.email}.</p>
                <Button onClick={() => { setForm(empty); setDone(false); }} variant="outline" className="mt-6 rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid="join-another-button">Submit another show</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="relative flex-1 flex flex-col gap-4">
                <h2 className="font-display text-2xl font-extrabold">{homepage.join_form_title || "Pitch your show"}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Your name *</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} className={field} data-testid="join-name-input" /></div>
                  <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={field} data-testid="join-email-input" /></div>
                </div>
                <div><Label>Show name</Label><Input value={form.show_name} onChange={(e) => set("show_name", e.target.value)} className={field} data-testid="join-show-input" /></div>
                <div className="flex-1 flex flex-col"><Label>Tell us about the show *</Label><Textarea required minLength={20} rows={5} value={form.pitch} onChange={(e) => set("pitch", e.target.value)} placeholder="What's it about, who's it for, how often do you publish, what makes it an underdog story?" className={`${field} flex-1 min-h-[140px] resize-none`} data-testid="join-pitch-input" /></div>
                <div><Label>Links</Label><Input value={form.links} onChange={(e) => set("links", e.target.value)} placeholder="RSS feed, Spotify, Apple, YouTube, socials…" className={field} data-testid="join-links-input" /></div>
                <Button type="submit" disabled={sending} className="w-full rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="join-submit-button">{sending ? "Sending…" : "Send my pitch"}</Button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
